import { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/modern/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { SURFACE_CONTROL_CLS, SURFACE_ICON_BTN_CLS } from "@/react/components/entityViews/files/constants/surfaceChrome";
import { cn } from "@/modern/utils";
import { Grid3x3, LayoutGrid, List, ZoomIn, ZoomOut } from "lucide-react";

type ViewMode = "grid" | "list";

interface DisplayPopoverProps {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  zoomLevel: number;
  zoomMax: number;
  zoomLabel: string;
  onZoomIn: () => void;
  onZoomOut: () => void;
}

/** Collapses view + zoom into one control so Media chrome stays one command row. */
export function DisplayPopover({
  viewMode,
  onViewModeChange,
  zoomLevel,
  zoomMax,
  zoomLabel,
  onZoomIn,
  onZoomOut,
}: DisplayPopoverProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Display options"
              aria-expanded={open}
              className={cn(SURFACE_ICON_BTN_CLS, "inline-flex items-center justify-center")}
            >
              <LayoutGrid className="size-3.5" aria-hidden />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">Display</TooltipContent>
      </Tooltip>

      <PopoverContent align="end" className="w-56 gap-0 p-0">
        <div className="border-b border-border/40 px-3 py-2">
          <p className="text-xs font-normal uppercase tracking-wide text-muted-foreground">View</p>
          <div
            role="group"
            aria-label="View mode"
            className={cn("mt-1.5 inline-flex w-full items-center gap-0.5 rounded-full p-0.5", SURFACE_CONTROL_CLS)}
          >
            <button
              type="button"
              onClick={() => onViewModeChange("list")}
              aria-pressed={viewMode === "list"}
              className={cn(
                "inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-full text-sm font-normal transition-[transform,colors] duration-150 ease-out active:scale-[0.98]",
                viewMode === "list"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <List className="size-3.5" aria-hidden />
              List
            </button>
            <button
              type="button"
              onClick={() => onViewModeChange("grid")}
              aria-pressed={viewMode === "grid"}
              className={cn(
                "inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-full text-sm font-normal transition-[transform,colors] duration-150 ease-out active:scale-[0.98]",
                viewMode === "grid"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <Grid3x3 className="size-3.5" aria-hidden />
              Grid
            </button>
          </div>
        </div>

        {viewMode === "grid" ? (
          <div className="px-3 py-2">
            <p className="text-xs font-normal uppercase tracking-wide text-muted-foreground">Size</p>
            <div className="mt-1.5 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={onZoomOut}
                disabled={zoomLevel === 0}
                aria-label="Zoom out"
                className={cn(
                  SURFACE_ICON_BTN_CLS,
                  "inline-flex items-center justify-center disabled:opacity-40",
                )}
              >
                <ZoomOut className="size-3.5" aria-hidden />
              </button>
              <span className="min-w-0 truncate text-sm font-normal text-foreground">{zoomLabel}</span>
              <button
                type="button"
                onClick={onZoomIn}
                disabled={zoomLevel === zoomMax}
                aria-label="Zoom in"
                className={cn(
                  SURFACE_ICON_BTN_CLS,
                  "inline-flex items-center justify-center disabled:opacity-40",
                )}
              >
                <ZoomIn className="size-3.5" aria-hidden />
              </button>
            </div>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
